import { Deserializer } from "jsonapi-serializer";
import { typeSystemStats } from "../constants/api";
import { IMPROX_READER_TAB_COUNT } from "../constants/system-constants";
import { Translator } from "../contexts/translator";
import { DeviceInput } from "../models/DeviceInput";
import { genericError } from "../models/GenericError";
import { ListQuery } from "../models/ListQuery";
import { Name } from "../models/Name";
import { PaginationModel } from "../models/Pagination";
import { DeviceInputWrapper } from "../models/wrappers/DeviceInputWrapper";
import { usedByStatus } from "../utilities/UsedByStatus";
import { APIConnector } from "./api/APIConnector";
import { createPagination } from "./utilities/paginationBuilder";
import { getConstraintViolation, processServerError } from "./utilities/ServiceError";
import { getDeviceSettingStatus } from "./utilities/StatusBuilder";
import { createPartialURL, getFilterClause } from "./utilities/urlBuilder";

const apiConnector = new APIConnector();

export const getDeviceInputs = async (itemId: string, translate: Translator, listParameters?: ListQuery): Promise<DeviceInputWrapper> => {
    try {
        const filter: string = getFilterClause(listParameters?.filters, listParameters?.searchTerm);
        const paginationString = createPartialURL(listParameters?.goToPage, listParameters?.pageSize, listParameters?.sortBy, filter);
        const settingFields = "fields[setting]=trnName,settingType,sDist,hierarchyType";
        const settingTypeFields = "fields[settingType]=trnName";
        const devInputFields = "fields[devInput]=setting,diNo";
        const include = "include=setting.settingType";
        const url = `deviceImprox/${itemId}/devInputs?${include}&${settingFields}&${settingTypeFields}&${devInputFields}${paginationString}`;
        const response = await apiConnector.fetchData(url);
        const json = await response.json();
        const pagination = createPagination(json.meta);

        return new Promise((resolve, reject) => {
            if (json.errors) {
                return reject({status: response.status, message: getConstraintViolation(json.errors[0].detail) })
            }
            new Deserializer().deserialize(json, (error: any, data: any[]) => {
                if (error) {
                    return reject({status: response.status, message: genericError });
                }

                const deviceInput: DeviceInput[] = data.map((deviceInput): DeviceInput => {
                    return {
                        id: deviceInput['id'],
                        name: deviceInput['setting'] === null ? '' : deviceInput['setting']['trn-name'],
                        type: deviceInput['setting'] === null ? '' : deviceInput['setting']['setting-type']['trn-name'],
                        usedBy: deviceInput['setting'] === null ? '' : usedByStatus(deviceInput['setting']['s-dist'], translate),
                        inputNumber: deviceInput['di-no'],
                        status: getDeviceSettingStatus(deviceInput['setting'] === null ? null : deviceInput['setting']['hierarchy-type'])
                    }
                })

                return resolve({ rows: deviceInput, pagination });
            });
        });
    } catch (error) {
        return new Promise((resolve, reject) => {
            reject(processServerError(error));
        });
    }
}

interface DeviceTabCount {
    actionTabCount: number;
    paramTabCount: number;
    inputTabCount: number;
    eventActionTabCount: number;
}

export const getDeviceInputTabCount = async (deviceId: string): Promise<DeviceTabCount> => {
    try {
        const url = '';        
        const headers = {
            type: typeSystemStats,
            statType: "PER_IMPROX_DEVICE",            
            deviceId:deviceId
        }
        const response = await apiConnector.fetchData(url, true, headers);
        const jsonRespone = await response.json();

        return new Promise((resolve, reject) => {
            if (jsonRespone.errors) {
                return reject({status: response.status, message: genericError})
            }

            let deviceTabCount: DeviceTabCount = {
                actionTabCount: 0, 
                paramTabCount: 0, 
                inputTabCount: 0, 
                eventActionTabCount: 0
            };

            for (let element of jsonRespone['totals']['counts']) {
                if (element['category'] === IMPROX_READER_TAB_COUNT.ACTIONS) {
                    deviceTabCount.actionTabCount = element['count']
                }
                else if (element['category'] === IMPROX_READER_TAB_COUNT.INPUTS) {
                    deviceTabCount.inputTabCount = element['count']
                }
                else if (element['category'] === IMPROX_READER_TAB_COUNT.PARAMETERS) {
                    deviceTabCount.paramTabCount = element['count']
                }
            }

            return resolve(deviceTabCount);
        });
    } catch (error) {
        return new Promise((resolve, reject) => {
            reject(processServerError(error));
        });
    }
}

export const getDeviceForSetting = async (settingId: string, listParameters?: ListQuery): Promise<{ rows: Name[], pagination: PaginationModel }> => {
    try {
        const filter: string = getFilterClause(listParameters?.filters, listParameters?.searchTerm);
        const paginationString = createPartialURL(listParameters?.goToPage, listParameters?.pageSize, listParameters?.sortBy, filter);
        const include = "include=devInputs.setting";
        const improxFields = "fields[terminal]=devName";
        const url = `deviceImprox?&filter=devInputs.setting.id==${settingId}&${include}&${improxFields}${paginationString}`;
        const response = await apiConnector.fetchData(url);
        const json = await response.json();
        const pagination = createPagination(json.meta);

        return new Promise((resolve, reject) => {
            if (json.errors) {
                return reject({status: response.status, message: getConstraintViolation(json.errors[0].detail) })
            }

            return new Deserializer().deserialize(json, (error: any, data: any[]) => {
                if (error) {
                    return reject({status: response.status, message: genericError });
                }
                
                const readers: Name[] = data.map((reader): Name => {
                    return {
                        value: reader['id'],
                        label: reader['dev-name']
                    }
                });

                return resolve({ rows: readers, pagination });
            })
        });
    } catch (error) {
        return Promise.reject(processServerError(error));
    }
}